import {
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
  Input,
  Label,
  Switch,
} from "@egaranti/components";

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import productActivationService from "@/services/productActivationService";

import FormFieldBuilder from "@/components/productActivation/formFieldBuilder";
import { useProductActivation } from "@/components/productActivation/useProductActivation";
import { Breadcrumb, BreadcrumbItem } from "@/components/shared/breadcrumbs";

const ProductActivation = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [logoPreview, setLogoPreview] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const {
    form,
    fields,
    isLoading,
    addFormField,
    remove,
    updateFieldOrder,
    onSubmit,
    existingActivation,
  } = useProductActivation();

  useEffect(() => {
    if (existingActivation?.logo) {
      setLogoPreview(existingActivation.logo);
    }
  }, [existingActivation]);

  const handleFileChange = (file, onChange) => {
    onChange(file);
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    } else {
      setLogoPreview(existingActivation?.logo || null);
    }
  };

  const handleDeleteLogo = (onChange) => {
    onChange(null);
    setLogoPreview(null);
  };

  const handleDownloadQRCodes = async () => {
    setIsDownloading(true);
    try {
      const response = await productActivationService.downloadQRCodesZip();
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "qr-codes.zip";
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error("ZIP indirme hatası:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const LogoPreview = ({ logoUrl, onDelete }) => {
    if (!logoUrl) return null;

    return (
      <div className="group relative mt-2">
        <div
          className="relative h-20 w-20 cursor-pointer overflow-hidden rounded-lg border border-gray-200"
          onClick={() => window.open(logoUrl, "_blank")}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              window.open(logoUrl, "_blank");
            }
          }}
          aria-label="Logo önizlemesi - tıklayarak büyük görüntüleyin"
        >
          <img
            src={logoUrl}
            alt="Logo preview"
            className="h-full w-full object-contain"
          />
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100"
          aria-label="Logoyu sil"
        >
          ×
        </button>
      </div>
    );
  };

  if (isLoading) {
    return (
      <main className="flex min-h-screen flex-col bg-[#F9FAFC]">
        <div className="container mx-auto my-20 flex flex-col items-center justify-center">
          <div className="text-center">Loading...</div>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen flex-col bg-[#F9FAFC]">
      <Breadcrumb>
        <BreadcrumbItem link="/settings/activations">
          {t("settingsPage.activations")}
        </BreadcrumbItem>
        <BreadcrumbItem active>
          {t("settingsPage.productRegisterActivation.title")}
        </BreadcrumbItem>
      </Breadcrumb>

      <div className="container mx-auto my-20 flex flex-col items-center justify-center gap-6">
        <div className="formBox">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              {/* Basic Settings Section */}
              <div className="mb-5 flex flex-col gap-5 rounded-lg border border-gray-200 p-5">
                <h2 className="mb-5 font-medium">{t("titles.settings")}</h2>
                {/*
                  EPS-5120 (7. bulgu) — "Ayarlar" bolumundeki dort alan (Marka,
                  URL, Redirect URL, Marka Rengi) hic sarmayan tek bir flex
                  satirindaydi. Replikada olculdu (390 px): Marka 38 px,
                  Redirect URL 54 px, Marka Rengi 39 px -- okunamayacak kadar
                  dar; masaustunde her biri 223 px.

                  Cozum: dar ekranda alt alta, md'den itibaren eski tek satir.
                */}
                <div className="flex flex-col gap-5 md:flex-row md:justify-between">
                  <FormField
                    control={form.control}
                    name="brand"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <Label>{t("settingsPage.brand")}</Label>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="subdomain"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <Label>URL</Label>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                        <FormDescription>
                          {form.watch("subdomain")}.egaranti.com.tr
                        </FormDescription>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="redirectUrl"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <Label>Redirect URL</Label>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="brandColor"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <Label>
                          {t(
                            "settingsPage.productRegisterActivation.brandColor",
                            { defaultValue: "Marka Rengi" },
                          )}
                        </Label>
                        <FormControl>
                          <Input placeholder="#000000" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="file"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <Label>Logo</Label>
                        <FormControl>
                          <Input
                            accept=".jpg, .jpeg, .png, .pdf"
                            type="file"
                            onChange={(e) =>
                              handleFileChange(
                                e.target.files ? e.target.files[0] : null,
                                field.onChange,
                              )
                            }
                          />
                        </FormControl>
                        <LogoPreview
                          logoUrl={logoPreview}
                          onDelete={() => handleDeleteLogo(field.onChange)}
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Document Display Selection */}
              <div className="mb-5 flex flex-col gap-5 rounded-lg border border-gray-200 p-5">
                <h2 className="font-medium">
                  {t("settingsPage.productRegisterActivation.section3.title")}
                </h2>
                <p className="text-sm text-[#667085]">
                  {t("settingsPage.productRegisterActivation.section3.desc")}
                </p>
                {/*
                  EPS-5120 (7. bulgu) — "Belgeler" bolumundeki uc kart da her
                  ekranda yan yana sikisiyordu. Replikada olculdu (390 px):
                  sutunlar `80.66px x3`, acma/kapama dugmeleri yazilarin ustune
                  biniyor; masaustunde 377 px x3.
                */}
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
                  <div className="flex justify-between rounded-lg border p-6">
                    <FormField
                      control={form.control}
                      name="warrantyFileOption"
                      render={({ field }) => (
                        <FormItem className="flex w-full items-center justify-between">
                          <Label>
                            {t(
                              "settingsPage.productRegisterActivation.section3.warranty",
                            )}
                          </Label>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="flex flex-col gap-4 rounded-lg border p-6">
                    <FormField
                      control={form.control}
                      name="billingFileOption"
                      render={({ field }) => (
                        <FormItem className="flex w-full items-center justify-between">
                          <Label>
                            {t(
                              "settingsPage.productRegisterActivation.section3.invoice",
                            )}
                          </Label>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={(checked) => {
                                field.onChange(checked);
                                // Fatura kapatılınca ibraz onayı da kapanır.
                                if (!checked) {
                                  form.setValue("billingWaiverOption", false);
                                }
                              }}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="billingWaiverOption"
                      render={({ field }) => (
                        <FormItem className="flex w-full items-center justify-between border-t pt-4">
                          <Label
                            className={
                              form.watch("billingFileOption")
                                ? "text-sm font-normal text-[#667085]"
                                : "text-sm font-normal text-gray-300"
                            }
                          >
                            {t(
                              "settingsPage.productRegisterActivation.section3.invoiceWaiver",
                              "Fatura ibraz edilemezse onay kutusu göster",
                            )}
                          </Label>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              disabled={!form.watch("billingFileOption")}
                              onCheckedChange={field.onChange}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="flex justify-between rounded-lg border p-6">
                    <FormField
                      control={form.control}
                      name="serviceFileOption"
                      render={({ field }) => (
                        <FormItem className="flex w-full items-center justify-between">
                          <Label>
                            {t(
                              "settingsPage.productRegisterActivation.section3.service",
                            )}
                          </Label>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </div>

              {/* Form Fields Builder */}
              <FormFieldBuilder
                fields={fields}
                addFormField={addFormField}
                updateFieldOrder={updateFieldOrder}
                removeField={remove}
                form={form}
              />
            </form>
          </Form>

          <footer className="flex justify-between gap-4">
            <Button
              variant="secondaryGray"
              className="mt-5"
              onClick={handleDownloadQRCodes}
              disabled={isDownloading}
            >
              {isDownloading
                ? t("common.loading")
                : t(
                    "settingsPage.productRegisterActivation.buttons.downloadQRCodes",
                    "QR Kodları İndir (ZIP)",
                  )}
            </Button>
            <div className="flex gap-4">
              <Button
                variant="secondaryGray"
                className="mt-5"
                onClick={() => navigate(-1)}
              >
                {t("common.cancel")}
              </Button>
              <Button
                className="mt-5"
                onClick={form.handleSubmit(onSubmit)}
                disabled={isLoading}
              >
                {isLoading ? "Kaydediliyor..." : t("common.save")}
              </Button>
            </div>
          </footer>
        </div>
      </div>
    </main>
  );
};

export default ProductActivation;
